import React, { useEffect, useMemo, useState } from "react";
import { Command as CommandPrimitive } from "cmdk";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Search,
  ArrowRight,
  LayoutGrid,
  ShoppingBag,
  BookOpen,
  Megaphone,
  Printer,
  Globe2,
  Headphones,
  Sparkles,
  CornerDownLeft,
  Command as CmdIcon,
} from "lucide-react";
import { Dialog, DialogContent } from "./ui/dialog";
import { SERVICES } from "../lib/services";

const ICONS = { BookOpen, Megaphone, Printer, Globe2, Headphones, Sparkles };

export default function CommandPalette({ open, onOpenChange }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [query, setQuery] = useState("");

  // Reset query when opened
  useEffect(() => {
    if (open) setQuery("");
  }, [open]);

  const go = (to) => {
    onOpenChange(false);
    // Defer so the dialog closes cleanly before route change
    setTimeout(() => navigate(to), 60);
  };

  const quickLinks = useMemo(
    () => [
      { id: "dashboard", label: "Studio Dashboard", hint: "Overview & metrics", to: "/dashboard", icon: LayoutGrid, keywords: "home overview studio dashboard live" },
      { id: "plan", label: "Your Plan", hint: "Review & submit the package", to: "/plan", icon: ShoppingBag, keywords: "plan cart checkout package" },
    ],
    []
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[640px] gap-0 overflow-hidden border-brand-hair bg-brand-panel p-0 text-brand-ivory shadow-2xl">
        <CommandPrimitive
          label="Studio search"
          className="flex h-full w-full flex-col overflow-hidden"
          shouldFilter
          filter={(value, search) => {
            const v = value.toLowerCase();
            const s = search.trim().toLowerCase();
            if (!s) return 1;
            // simple contains; boost exact prefix
            if (v.startsWith(s)) return 1;
            if (v.includes(s)) return 0.6;
            // token contains
            const tokens = s.split(/\s+/);
            const hit = tokens.every((t) => v.includes(t));
            return hit ? 0.4 : 0;
          }}
        >
          <div className="flex items-center gap-3 border-b border-brand-hair px-4">
            <Search className="h-4 w-4 shrink-0 text-brand-mute" />
            <CommandPrimitive.Input
              value={query}
              onValueChange={setQuery}
              placeholder="Search services, sub-services, or actions…"
              className="h-14 w-full bg-transparent font-serif-display text-[18px] text-brand-ivory placeholder:text-brand-mute focus:outline-none"
            />
            <kbd className="hidden items-center gap-1 rounded border border-brand-hair px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-widest text-brand-mute sm:flex">
              ESC
            </kbd>
          </div>

          <CommandPrimitive.List className="max-h-[420px] overflow-y-auto px-2 py-3">
            <CommandPrimitive.Empty className="px-4 py-10 text-center">
              <div className="font-serif-display text-[18px] text-brand-ivory">
                Nothing matches “{query}”.
              </div>
              <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">
                Try a service, sub-service, or “plan”.
              </div>
            </CommandPrimitive.Empty>

            <CommandPrimitive.Group
              heading="Quick Jump"
              className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-2 [&_[cmdk-group-heading]]:font-mono [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-[0.28em] [&_[cmdk-group-heading]]:text-brand-mute"
            >
              {quickLinks.map((q) => {
                const active = location.pathname === q.to;
                const Icon = q.icon;
                return (
                  <PaletteItem
                    key={q.id}
                    value={`${q.label} ${q.hint} ${q.keywords}`}
                    active={active}
                    onSelect={() => go(q.to)}
                    left={
                      <div className="flex h-8 w-8 items-center justify-center rounded-md bg-brand/10 text-brand">
                        <Icon className="h-4 w-4" />
                      </div>
                    }
                    title={q.label}
                    subtitle={q.hint}
                    trailing="Open"
                  />
                );
              })}
            </CommandPrimitive.Group>

            <CommandPrimitive.Group
              heading="Services"
              className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-2 [&_[cmdk-group-heading]]:font-mono [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-[0.28em] [&_[cmdk-group-heading]]:text-brand-mute"
            >
              {SERVICES.map((svc) => {
                const Icon = ICONS[svc.icon] || BookOpen;
                return (
                  <PaletteItem
                    key={svc.slug}
                    value={`${svc.id} ${svc.label} ${svc.tagline} ${svc.description}`}
                    onSelect={() => go(`/service/${svc.slug}`)}
                    left={
                      <div className="flex h-8 w-8 items-center justify-center rounded-md bg-brand/10 text-brand">
                        <Icon className="h-4 w-4" />
                      </div>
                    }
                    code={svc.id}
                    title={svc.label}
                    subtitle={svc.tagline}
                    trailing={`${svc.subs.length} options`}
                  />
                );
              })}
            </CommandPrimitive.Group>

            <CommandPrimitive.Group
              heading="Sub-Services"
              className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-2 [&_[cmdk-group-heading]]:font-mono [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-[0.28em] [&_[cmdk-group-heading]]:text-brand-mute"
            >
              {SERVICES.flatMap((svc) =>
                svc.subs.map((sub) => {
                  const Icon = ICONS[svc.icon] || BookOpen;
                  return (
                    <PaletteItem
                      key={sub.code}
                      value={`${sub.code} ${sub.title} ${sub.subtitle || ""} ${svc.label} ${sub.description || ""}`}
                      onSelect={() => go(`/service/${svc.slug}/${sub.slug}`)}
                      left={
                        <div className="flex h-8 w-8 items-center justify-center rounded-md border border-brand-hair text-brand-ivory/80">
                          <Icon className="h-3.5 w-3.5" />
                        </div>
                      }
                      code={sub.code}
                      title={sub.title}
                      subtitle={`${svc.label}${sub.subtitle ? ` · ${sub.subtitle}` : ""}`}
                      trailing={sub.price}
                    />
                  );
                })
              )}
            </CommandPrimitive.Group>
          </CommandPrimitive.List>

          <div className="flex items-center justify-between border-t border-brand-hair px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center gap-1.5">
                <KeyCap>↑</KeyCap>
                <KeyCap>↓</KeyCap>
                Navigate
              </span>
              <span className="inline-flex items-center gap-1.5">
                <KeyCap>
                  <CornerDownLeft className="h-2.5 w-2.5" />
                </KeyCap>
                Select
              </span>
            </div>
            <span className="inline-flex items-center gap-1.5">
              <KeyCap>
                <CmdIcon className="h-2.5 w-2.5" />
              </KeyCap>
              <KeyCap>K</KeyCap>
              Toggle
            </span>
          </div>
        </CommandPrimitive>
      </DialogContent>
    </Dialog>
  );
}

function PaletteItem({ value, onSelect, left, code, title, subtitle, trailing, active }) {
  return (
    <CommandPrimitive.Item
      value={value}
      onSelect={onSelect}
      className={`group my-0.5 flex cursor-pointer items-center gap-3 rounded-md px-3 py-2.5 text-left transition-colors aria-selected:bg-brand/12 aria-selected:text-brand-ivory ${
        active ? "border border-brand/40" : ""
      }`}
    >
      {left}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          {code && (
            <span className="font-mono text-[10px] tracking-[0.22em] text-brand-mute group-aria-selected:text-brand">
              {code}
            </span>
          )}
          <span className="truncate font-serif-display text-[15px] leading-tight tracking-tight text-brand-ivory">
            {title}
          </span>
        </div>
        {subtitle && (
          <div className="mt-0.5 truncate text-[11.5px] text-brand-ivory/55">{subtitle}</div>
        )}
      </div>
      {trailing && (
        <div className="ml-auto flex items-center gap-2 font-mono text-[10.5px] uppercase tracking-[0.22em] text-brand-mute">
          <span className="whitespace-nowrap">{trailing}</span>
          <ArrowRight className="h-3.5 w-3.5 opacity-0 transition-opacity group-aria-selected:opacity-100" />
        </div>
      )}
    </CommandPrimitive.Item>
  );
}

function KeyCap({ children }) {
  return (
    <kbd className="flex h-4 min-w-4 items-center justify-center rounded border border-brand-hair px-1 text-[9px] text-brand-ivory/80">
      {children}
    </kbd>
  );
}
